import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  LayoutDashboard,
  Workflow,
  BookOpen,
  Handshake,
  Package,
  Truck,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  User,
  ChevronDown,
  TrendingUp,
} from 'lucide-react';
import AuthService from '../../services/AuthService';
import { getCookie } from '../../services/DataRequestService';
import demandEdgeLogo from '../../assets/brand/demand-edge-logo-preview.png';
import demandEdgeIcon from '../../assets/brand/demand-edge-icon.png';

const links = [
  { to: '/Dashboard', name: 'Dashboard', state: { pageName: 'Dashboard' }, icon: LayoutDashboard },
  { to: '/CreateWorkflow', name: 'Create Workflow', state: { pageName: 'Create Workflow' }, icon: Workflow },
  { to: '/ManageWorkflow', name: 'Forecast Library', state: { pageName: 'Forecast Library' }, icon: BookOpen },
  { to: '/comparison', name: 'Collaboration Portal', state: { pageName: 'Collaboration Portal' }, icon: Handshake },
  { to: '/indentation', name: 'Inventory Optimization', state: { pageName: 'Inventory Optimization' }, icon: Package },
  // {
  //   to: '/SupplyChainManagement',
  //   name: 'Supply Chain Management',
  //   state: { pageName: 'Supply Chain Management' },
  //   icon: Truck,
  // },
  {
    to: '/trend-conjoint',
    name: 'Trend Analysis',
    state: { pageName: 'Trend Analysis' },
    icon: TrendingUp,
  },
  {
    to: '#',
    name: 'Configuration',
    state: { pageName: 'Configuration' },
    icon: Settings,
    children: [
      { to: '/sales-person', name: 'Sales Person', state: { pageName: 'Sales Person List' } },
      { to: '/sales-person-sku-mapping', name: 'Sales Person Sku Mapping', state: { pageName: 'Sales Person' } },
      // { to: '/management', name: 'Management', state: { pageName: 'Management' } },
      // { to: '/vendor-config', name: 'Vendor Config', state: { pageName: 'Vendor Config' } },
      { to: '/audit-logs', name: 'Audit Logs', state: { pageName: 'Audit Logs' } },
    ],
  },
];

export default function Navbar({ collapsed = false, onToggle }) {
  const location = useLocation();
  const [activeLink, setActiveLink] = useState('');
  const [openSubmenu, setOpenSubmenu] = useState('');
  const username = getCookie('username')?.toUpperCase() || 'USER';
  const configItemRef = useRef(null);
  const scrollToConfigRef = useRef(false);

  // After expand from config icon click: scroll nav to Configuration (not top)
  useEffect(() => {
    if (collapsed || !scrollToConfigRef.current) return;
    scrollToConfigRef.current = false;
    const timer = setTimeout(() => {
      configItemRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }, 280);
    return () => clearTimeout(timer);
  }, [collapsed, openSubmenu]);

  const isActive = (path) => {
    if (!path || path === '#') return false;
    const current = location.pathname.toLowerCase();
    const target = path.toLowerCase();
    return (
      current === target ||
      current === target.replace(/^\//, '') ||
      current.startsWith(`${target}/`)
    );
  };

  const isLinkActive = (link) => {
    if (isActive(link.to)) return true;
    return link.children?.some((child) => isActive(child.to));
  };

  const doLogout = () => {
    AuthService.logout();
  };

  return (
    <Sidebar $collapsed={collapsed}>
      <div className="de-sidebar-inner">
        <div className="de-sidebar-brand">
          <a href="/" aria-label="Demand Edge" className="de-sidebar-brand-link">
            {collapsed ? (
              <span className="de-sidebar-logo-card de-sidebar-logo-card--icon">
                <img
                  src={demandEdgeIcon}
                  className="de-sidebar-logo-icon"
                  alt="Demand Edge"
                />
              </span>
            ) : (
              <span className="de-sidebar-logo-card">
                <img
                  src={demandEdgeLogo}
                  className="de-sidebar-logo"
                  alt="Demand Edge"
                />
              </span>
            )}
          </a>
          <button
            type="button"
            className="de-sidebar-toggle"
            onClick={onToggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        <div className="de-sidebar-user">
          <div className="de-sidebar-user-icon">
            <User size={20} strokeWidth={2} />
          </div>
          {!collapsed && (
            <div className="de-sidebar-user-text">
              <span>Welcome to Demand Edge</span>
              <strong>{username}</strong>
            </div>
          )}
        </div>

        <button
          type="button"
          className="de-sidebar-logout"
          onClick={doLogout}
          title="Logout"
        >
          <LogOut size={18} strokeWidth={2} />
          {!collapsed && <span>Logout</span>}
        </button>

        {!collapsed && <p className="de-sidebar-nav-label">Navigation</p>}

        <nav className="de-sidebar-nav" aria-label="Main navigation">
          <ul>
            {links.map((link) => {
              const Icon = link.icon;
              const active = isLinkActive(link) || activeLink === link.name;
              const hasChildren = link.children?.length > 0;
              const submenuOpen = openSubmenu === link.name || active;

              return (
                <li
                  key={link.name}
                  className={hasChildren ? 'has-children' : ''}
                  ref={link.name === 'Configuration' ? configItemRef : null}
                >
                  {hasChildren ? (
                    <button
                      type="button"
                      className={`de-sidebar-nav-parent ${active ? 'active' : ''}`}
                      onClick={() => {
                        // When collapsed: expand navbar, open Configuration, scroll to that part
                        if (collapsed) {
                          scrollToConfigRef.current = true;
                          onToggle?.();
                          setOpenSubmenu(link.name);
                          return;
                        }
                        setOpenSubmenu(openSubmenu === link.name ? '' : link.name);
                      }}
                      title={link.name}
                    >
                      <Icon size={19} strokeWidth={2} />
                      {!collapsed && (
                        <>
                          <span>{link.name}</span>
                          <ChevronDown
                            size={16}
                            className={`de-sidebar-chevron ${submenuOpen ? 'open' : ''}`}
                          />
                        </>
                      )}
                    </button>
                  ) : (
                    <Link
                      to={link.to}
                      state={link.state}
                      onClick={() => setActiveLink(link.name)}
                      className={active ? 'active' : ''}
                      title={link.name}
                    >
                      <Icon size={19} strokeWidth={2} />
                      {!collapsed && <span>{link.name}</span>}
                    </Link>
                  )}

                  {hasChildren && submenuOpen && !collapsed && (
                    <ul className="de-sidebar-submenu">
                      {link.children.map((child) => (
                        <li key={child.name}>
                          <Link
                            to={child.to}
                            state={child.state}
                            onClick={() => setActiveLink(child.name)}
                            className={isActive(child.to) || activeLink === child.name ? 'active' : ''}
                          >
                            {child.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </Sidebar>
  );
}

const Sidebar = styled.aside`
  --de-navy: #032b4e;
  --de-navy-dark: #021f3a;
  --de-gold: #ffe082;
  --de-gold-hover: #ffd54f;
  --de-red: #ac1424;
  --de-sidebar-width: 272px;
  --de-sidebar-collapsed: 88px;

  position: fixed;
  top: 0;
  left: 0;
  z-index: 200;
  width: ${({ $collapsed }) => ($collapsed ? 'var(--de-sidebar-collapsed)' : 'var(--de-sidebar-width)')};
  height: 100vh;
  background: linear-gradient(180deg, var(--de-navy) 0%, var(--de-navy-dark) 100%);
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 4px 0 24px rgba(3, 43, 78, 0.18);
  transition: width 0.25s ease;
  overflow: hidden;

  ${({ $collapsed }) =>
    $collapsed &&
    `
    .de-sidebar-inner {
      padding: 14px 12px 20px;
      align-items: center;
    }

    .de-sidebar-brand {
      flex-direction: column;
      align-items: center;
      gap: 10px;
      width: 100%;
      margin-bottom: 14px;
      padding-bottom: 12px;
    }

    .de-sidebar-brand-link {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .de-sidebar-toggle {
      width: 42px;
      height: 42px;
    }

    .de-sidebar-user {
      width: 46px;
      height: 46px;
      padding: 0;
      margin: 0 0 10px;
      background: transparent;
      border: none;
      justify-content: center;
    }

    .de-sidebar-user-icon {
      width: 46px;
      height: 46px;
      min-width: 46px;
    }

    .de-sidebar-logout {
      width: 46px;
      height: 46px;
      padding: 0;
      margin: 0 0 14px;
    }

    .de-sidebar-nav {
      width: 100%;
      padding-right: 0;
      scrollbar-width: none;
    }

    .de-sidebar-nav::-webkit-scrollbar {
      width: 0;
      height: 0;
      display: none;
    }

    .de-sidebar-nav ul {
      align-items: center;
      gap: 12px;
    }

    .de-sidebar-nav a,
    .de-sidebar-nav-parent {
      width: 48px;
      height: 48px;
      padding: 0;
      gap: 0;
      justify-content: center;
      box-sizing: border-box;
      border: none;
    }

    .de-sidebar-nav a.active,
    .de-sidebar-nav-parent.active {
      outline: none;
      border: none;
      background: rgba(27, 133, 186, 0.35);
      color: var(--de-gold);
      box-shadow: inset 3px 0 0 var(--de-gold);
    }

    .de-sidebar-nav a:hover,
    .de-sidebar-nav-parent:hover {
      border: none;
    }
  `}

  .de-sidebar-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 20px 14px 24px;
  }

  .de-sidebar-brand {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 24px;
    padding-bottom: 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .de-sidebar-brand-link {
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 1;
    text-decoration: none;
  }

  .de-sidebar-logo-card {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 210px;
    height: fit-content;
    padding: 6px 10px;
    background: #f7f8fa;
    border: 1px solid rgba(255, 255, 255, 0.45);
    border-radius: 8px;
    box-sizing: border-box;
  }

  .de-sidebar-logo {
    display: block;
    width: 100%;
    height: auto;
    max-height: 48px;
    object-fit: contain;
  }

  .de-sidebar-logo-card--icon {
    width: 46px;
    height: 46px;
    max-width: 46px;
    padding: 4px;
  }

  .de-sidebar-logo-icon {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .de-sidebar-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.06);
    color: #fff;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.2s ease, border-color 0.2s ease;
  }

  .de-sidebar-toggle:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.25);
  }

  .de-sidebar-user {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    margin-bottom: 14px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
  }

  .de-sidebar-user-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    min-width: 40px;
    border-radius: 10px;
    background: #1b85ba;
    color: #fff;
  }

  .de-sidebar-user-text {
    min-width: 0;
  }

  .de-sidebar-user-text span {
    display: block;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.65);
    line-height: 1.3;
    margin-bottom: 2px;
  }

  .de-sidebar-user-text strong {
    display: block;
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .de-sidebar-logout {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 11px 14px;
    margin-bottom: 22px;
    border: none;
    border-radius: 10px;
    background: #c9552a;
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease;
  }

  .de-sidebar-logout:hover {
    background: #a84420;
    transform: translateY(-1px);
  }

  .de-sidebar-nav-label {
    margin: 0 0 16px 6px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.45);
  }

  .de-sidebar-nav {
    flex: 1;
    overflow-y: auto;
    padding-right: 6px;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.34) transparent;
  }

  .de-sidebar-nav::-webkit-scrollbar {
    width: 6px;
  }

  .de-sidebar-nav::-webkit-scrollbar-track {
    background: transparent;
  }

  .de-sidebar-nav::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.28);
    border-radius: 999px;
    border: 1px solid transparent;
    background-clip: padding-box;
  }

  .de-sidebar-nav::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.42);
  }

  .de-sidebar-nav ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .de-sidebar-nav a,
  .de-sidebar-nav-parent {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 14px;
    min-height: 46px;
    border-radius: 11px;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.88);
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
  }

  .de-sidebar-nav a span,
  .de-sidebar-nav-parent > span:first-of-type {
    flex: 1;
    text-align: left;
    line-height: 1.4;
  }

  .de-sidebar-nav a:hover,
  .de-sidebar-nav-parent:hover {
    background: rgba(255, 255, 255, 0.08);
    color: var(--de-gold);
  }

  .de-sidebar-nav a.active,
  .de-sidebar-nav-parent.active {
    background: rgba(27, 133, 186, 0.35);
    color: var(--de-gold);
    box-shadow: inset 3px 0 0 var(--de-gold);
  }

  .de-sidebar-nav a svg,
  .de-sidebar-nav-parent svg {
    flex-shrink: 0;
  }

  .de-sidebar-chevron {
    margin-left: auto;
    flex-shrink: 0;
    transition: transform 0.2s ease;
    opacity: 0.75;
  }

  .de-sidebar-chevron.open {
    transform: rotate(180deg);
  }

  .de-sidebar-submenu {
    margin: 8px 0 0 0;
    padding: 0 0 0 8px;
    gap: 10px !important;
  }

  .de-sidebar-submenu a {
    padding: 10px 12px 10px 40px;
    font-size: 12.5px;
    font-weight: 500;
    min-height: 42px;
  }

  @media (max-width: 767px) {
    width: var(--de-sidebar-collapsed);

    .de-sidebar-inner {
      padding: 14px 12px 20px;
      align-items: center;
    }

    .de-sidebar-brand {
      flex-direction: column;
      align-items: center;
      gap: 10px;
    }

    .de-sidebar-brand-link {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .de-sidebar-user {
      width: 46px;
      height: 46px;
      padding: 0;
      background: transparent;
      border: none;
      justify-content: center;
    }

    .de-sidebar-user-text,
    .de-sidebar-nav-label,
    .de-sidebar-nav a span,
    .de-sidebar-logout span {
      display: none;
    }

    .de-sidebar-logout {
      width: 46px;
      height: 46px;
      padding: 0;
    }

    .de-sidebar-nav {
      padding-right: 0;
      scrollbar-width: none;
    }

    .de-sidebar-nav::-webkit-scrollbar {
      width: 0;
      height: 0;
      display: none;
    }

    .de-sidebar-nav a,
    .de-sidebar-nav-parent {
      width: 48px;
      height: 48px;
      padding: 0;
      justify-content: center;
      gap: 0;
      box-sizing: border-box;
      border: none;
    }

    .de-sidebar-nav a.active,
    .de-sidebar-nav-parent.active {
      outline: none;
      border: none;
      background: rgba(27, 133, 186, 0.35);
      color: var(--de-gold);
      box-shadow: inset 3px 0 0 var(--de-gold);
    }
  }
`;
